import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { getPalUxGenieUrl } from '../../utils';

export interface UXGenieShareButtonProps {
  /** Template display name — used to build the URL slug */
  templateName: string;
  /** aiworker_usage_id */
  usageId: string;
  className?: string;
  title?: string;
}

/**
 * Copies the PAL UXGenie share URL to the clipboard.
 * URL: /ux-genie/:formSlug/:usageId
 *
 * Can be reused in the PAL app later with the same props.
 */
export const UXGenieShareButton: React.FC<UXGenieShareButtonProps> = ({
  templateName,
  usageId,
  className = '',
  title,
}) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const handleShare = useCallback(() => {
    if (!usageId || !templateName) return;

    const url = getPalUxGenieUrl(templateName, usageId);
    navigator.clipboard.writeText(url).then(() => {
      setIsCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
    }).catch(() => {
      // Clipboard API unavailable (e.g. non-secure context)
    });
  }, [usageId, templateName]);

  if (!usageId) return null;

  return (
    <button
      type="button"
      onClick={handleShare}
      title={title || t('Copy template link')}
      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${
        isCopied
          ? 'border-green-300 bg-green-50 text-green-700'
          : 'border-default-200 bg-content1 text-default-500 hover:border-default-300 hover:bg-default-100'
      } ${className}`}
    >
      <Icon icon={isCopied ? 'mdi:check' : 'mdi:share-variant-outline'} width={14} />
      {isCopied ? t('Copied!') : t('Share')}
    </button>
  );
};
